// May an incoming tournament snapshot be applied to the tournament this screen
// currently holds open?
//
// The question exists because the two are not always the same tournament. The
// real-time listener in js/app.js (setupActiveTournamentSync) is subscribed to
// ONE tournament id, chosen when the arbiter opened it — but the state it writes
// into, `state.activeTournament` plus the `state.players`/`state.rounds` mirror,
// is swapped out from under it by watchTournament() the moment the arbiter
// spectates someone else's tournament. Nothing re-points or drops the
// subscription when that happens.
//
// So a snapshot of the arbiter's OWN tournament can arrive while the mirror
// holds a WATCHED tournament's roster and results, and applying it merges the
// two: the incoming roster is empty for a tournament that has no players yet, so
// the "never blank out local players" guard keeps the watched roster, and
// mergeRounds keeps the watched rounds because their structure revision is the
// higher one. What comes out carries the arbiter's own name, dates and id with a
// stranger's players and games inside it — and the apply path writes that
// straight to localStorage, past the spectator check in saveToLocalStorage(). The
// next ordinary save pushes it to Firestore, and the arbiter's tournament has
// been overwritten by the one they were only watching.
//
// The rule is therefore about identity, not about spectating as such: a snapshot
// describes the tournament it was subscribed to and may only ever be applied to
// that same tournament. Spectator mode is checked as well because an arbiter can
// open a spectator link to their OWN tournament, where the ids match but the
// mirror belongs to the read-only view rather than to the editing session.
//
// THE SCAN-RESULT LISTENER (handleScanSubmissions in js/app.js) has the identical
// shape and uses this same guard. It is subscribed per scan token while one
// tournament is open and is not torn down when that tournament is swapped out
// either, and its handler does more than merge: it edits state.rounds, it can
// pair a whole round, and it reports back to the phone. That last part is what
// made it destructive rather than merely wrong — a submission was marked
// 'applied' whatever happened, so a result sent from a phone while the arbiter
// was watching another tournament was recorded as handled without ever reaching
// a round, and the phone does not offer it again.
//
// Declining a delivery never loses one, because leaving a watch re-establishes
// these subscriptions and a fresh subscription opens with the full current set.
//
// Pure and dependency-free so the cases can be tested directly; the caller
// supplies the facts.

/**
 * @param {object} facts
 * @param {string} facts.snapshotId       the tournament id this listener follows
 * @param {string} facts.activeId         id of the tournament state currently holds
 * @param {boolean} facts.isSpectatorMode this screen is watching, not arbitering
 * @returns {boolean} true when the snapshot describes the open tournament and may
 *                    be merged into it; false when it must be ignored.
 */
export function snapshotAppliesToActive({ snapshotId, activeId, isSpectatorMode }) {
    if (isSpectatorMode) return false;
    if (!snapshotId || !activeId) return false;
    return String(snapshotId) === String(activeId);
}
